import { useEffect, useMemo, useState } from "react";
import api, { num, formatApiError, maybeFunnelPrompt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NonSvoltaDialog from "./NonSvoltaDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import ProspectRecontactCard from "@/components/ProspectRecontactCard";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ban, Loader2, BarChart3, CheckCircle2, MapPin, Phone, CalendarClock, ClipboardCheck, Sparkles, Clock, FileClock, ShieldCheck, Radar } from "lucide-react";
import VisitaB2BDialog from "@/components/VisitaB2BDialog";
import { VisiteDaProgrammareDialog } from "@/components/VisiteDaProgrammareDialog";
import QuickAdd from "@/components/QuickAdd";
import ProspectRecontactDialog from "@/components/ProspectRecontactDialog";
import RecallPreventiviDialog from "@/components/RecallPreventiviDialog";
import { buildDaProgrammare } from "@/lib/daProgrammare";
import { agentHsl, registerAgents } from "@/lib/agentColors";
import { Link, useSearchParams } from "react-router-dom";
import AffiancamentoButton from "@/components/AffiancamentoButton";

const TYPE_LABEL = { affiancamento_agenzia: "Affiancamento agenzia", visita_partner: "Visita", telefonata: "Telefonata", videocall: "Video call", email_contatto: "Email / contatto", review_partner: "Review Partner" };
const AGENCY_TYPES = ["telefonata", "videocall", "email_contatto"];
const MANAGER_TYPES = ["affiancamento_agenzia", "telefonata", "videocall", "email_contatto"];
const CONTACT_TYPES = ["telefonata", "videocall", "email_contatto"];

export const ACTOR_THEME = {
  agenzia: { label: "Agenzia", dot: "bg-blue-500", badge: "border-blue-300 bg-blue-100 text-blue-800", border: "border-l-blue-500", ring: "ring-blue-500" },
  responsabile_commerciale: { label: "Resp. Commerciale", dot: "bg-orange-500", badge: "border-orange-300 bg-orange-100 text-orange-800", border: "border-l-orange-500", ring: "ring-orange-500" },
};

const KPI = ({ icon: Icon, label, value, dot, onClick, testid }) => (
  <Card className={`p-5 ${onClick ? "cursor-pointer transition-colors hover:border-primary/60" : ""}`} onClick={onClick} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`flex h-8 w-8 items-center justify-center rounded-md text-white ${dot}`}><Icon className="h-4 w-4" /></div>
    </div>
    <div className="mt-3 font-heading text-3xl font-black tracking-tight">{value}</div>
  </Card>
);

const scoreText = (pct, greenAt, amberAt) =>
  pct == null ? "" : pct >= greenAt ? "text-emerald-400" : pct >= amberAt ? "text-amber-400" : "text-red-400";
const scoreBg = (pct, greenAt, amberAt) =>
  pct == null ? "bg-muted-foreground" : pct >= greenAt ? "bg-emerald-500" : pct >= amberAt ? "bg-amber-500" : "bg-red-500";

const ScoreKPI = ({ icon: Icon, label, value, hint, pct, greenAt, amberAt, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`flex h-8 w-8 items-center justify-center rounded-md text-white ${scoreBg(pct, greenAt, amberAt)}`}><Icon className="h-4 w-4" /></div>
    </div>
    <div className={`mt-3 font-heading text-3xl font-black tracking-tight ${scoreText(pct, greenAt, amberAt)}`}>{value}</div>
    {hint && <div className="mt-1 text-[10px] leading-tight text-muted-foreground">{hint}</div>}
  </Card>
);

export default function ActivitiesBoard({ actor, title, subtitle, showMonitor = false, readOnly = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canAssign = ["admin", "back_office"].includes(user?.role);
  const canDelete = ["admin", "back_office"].includes(user?.role) || user?.role_actual === "back_office";
  const theme = ACTOR_THEME[actor];
  const types = actor === "agenzia" ? AGENCY_TYPES : MANAGER_TYPES;
  const EMPTY = { type: types[0], date: "", time: "", agency_id: "none", partner_id: "none", prospect_id: "none", target_kind: "partner", decision_maker: false, status: "pianificato", outcome: "", notes: "", owner_id: "none", actor, prossima_azione: "", data_prossima_azione: "" };

  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [managers, setManagers] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [monitor, setMonitor] = useState(null);
  const [monitorPeriod, setMonitorPeriod] = useState("mese");
  const [visitaOpen, setVisitaOpen] = useState(false);
  const [kpiSel, setKpiSel] = useState(null);
  const [checkingNote, setCheckingNote] = useState(false);
  const [noteFeedback, setNoteFeedback] = useState(null);
  const [stats, setStats] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [showDaProg, setShowDaProg] = useState(false);
  const [reactItems, setReactItems] = useState([]);
  const [showProspect, setShowProspect] = useState(false);
  const [showRecall, setShowRecall] = useState(false);
  const [recallItems, setRecallItems] = useState([]);
  const [visitaDefault, setVisitaDefault] = useState({ partnerId: null, prospectId: null });
  const [qaOpen, setQaOpen] = useState(false);
  const [qaDefault, setQaDefault] = useState({ partnerId: null, partnerName: "", prospectId: null, prospectName: "" });
  const [fltAgent, setFltAgent] = useState("all");
  const [fltType, setFltType] = useState("all");
  const [fltFrom, setFltFrom] = useState("");
  const [fltTo, setFltTo] = useState("");
  const [progActs, setProgActs] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const checkNote = async (text) => {
    const t = (text ?? form.notes ?? "").trim();
    if (t.length < 10) { setNoteFeedback(null); return; }
    setCheckingNote(true);
    try { const r = await api.post("/activities/check-note", { text: t }); setNoteFeedback(r.data); }
    catch { setNoteFeedback(null); }
    finally { setCheckingNote(false); }
  };

  // Verifica AND automatica della nota (debounce mentre si scrive), senza pulsante
  useEffect(() => {
    if (!dialog) return;
    const t = (form.notes || "").trim();
    if (t.length < 10) { setNoteFeedback(null); return; }
    const id = setTimeout(() => checkNote(t), 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.notes, dialog]);

  const load = async () => {
    setLoading(true);
    const reqs = [api.get(`/activities?actor=${actor}&limit=1000`), api.get("/agencies"), api.get("/partners")];
    if (isAdmin) reqs.push(api.get("/users?role=commercial_manager"));
    const [a, ag, p, m] = await Promise.all(reqs);
    const d = a.data;
    setItems(d.items || d);
    setStats(d.stats || null);
    setTotalCount(d.total ?? (d.items || d).length);
    setAgencies(ag.data); setPartners(p.data); registerAgents((ag.data || []).map((x) => x.name));
    api.get("/prospects").then((r) => setProspects(r.data || [])).catch(() => setProspects([]));
    if (m) setManagers(m.data);
    if (canAssign) api.get("/assignees").then((r) => setAssignees(r.data || [])).catch(() => {});
    setLoading(false);
  };
  useEffect(() => { load(); }, [actor]);
  useEffect(() => {
    api.get(`/activities/scorecard-kpi?actor=${actor}`).then((r) => setScorecard(r.data)).catch(() => setScorecard(null));
  }, [actor]);
  useEffect(() => {
    if (searchParams.get("visite") === "1") {
      setShowDaProg(true);
      const sp = new URLSearchParams(searchParams);
      sp.delete("visite");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    api.get("/agenda/riattivazione").then((r) => setReactItems(r.data.items || [])).catch(() => setReactItems([]));
    api.get("/activities?limit=3000").then((r) => setProgActs(r.data.items || r.data)).catch(() => setProgActs([]));
  }, []);
  const refreshProg = () => api.get("/activities?limit=3000").then((r) => setProgActs(r.data.items || r.data)).catch(() => {});
  const daProgrammare = useMemo(() => buildDaProgrammare(reactItems, progActs), [progActs, reactItems]);
  const prospectDue = useMemo(() => {
    const t = new Date().toISOString().slice(0, 10);
    const HIDDEN = ["vinto", "ordine", "perso"];
    return (prospects || [])
      .filter((p) => p.next_action_date && String(p.next_action_date).slice(0, 10) <= t && !HIDDEN.includes(p.stage))
      .sort((a, b) => String(a.next_action_date).localeCompare(String(b.next_action_date)));
  }, [prospects]);
  useEffect(() => { api.get("/quotes/recall").then((r) => setRecallItems(r.data.items || [])).catch(() => setRecallItems([])); }, []);
  const actAgents = useMemo(() => {
    const byKey = {};
    (items || []).forEach((it) => {
      const key = it.agency_id || it.owner_id || "none";
      const label = it.resolved_agency_name || it.owner_name || "—";
      if (!byKey[key]) byKey[key] = label;
    });
    return Object.entries(byKey).map(([id, nm]) => [id, nm]).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [items]);
  const filteredActs = useMemo(() => {
    return (items || []).filter((it) => {
      if (fltAgent !== "all" && (it.agency_id || it.owner_id || "none") !== fltAgent) return false;
      if (fltType !== "all" && it.type !== fltType) return false;
      const d = (it.date || "").slice(0, 10);
      if (fltFrom && d < fltFrom) return false;
      if (fltTo && d > fltTo) return false;
      return true;
    });
  }, [items, fltAgent, fltType, fltFrom, fltTo]);
  const openAttivitaFor = (rec) => {
    setShowDaProg(false); setShowProspect(false);
    setQaDefault({ partnerId: rec.kind === "partner" ? rec.id : null, partnerName: rec.kind === "partner" ? rec.name : "", prospectId: rec.kind === "prospect" ? rec.id : null, prospectName: rec.kind === "prospect" ? rec.name : "" });
    setQaOpen(true);
  };
  const openVisitaFor = (rec) => {
    setShowDaProg(false); setShowProspect(false);
    setVisitaDefault({ partnerId: rec.kind === "partner" ? rec.id : null, prospectId: rec.kind === "prospect" ? rec.id : null });
    setVisitaOpen(true);
  };
  useEffect(() => {
    if (showMonitor) api.get(`/commercial/agencies-monitor?period=${monitorPeriod}`).then((r) => setMonitor(r.data)).catch(() => {});
  }, [monitorPeriod, showMonitor]);

  const k = useMemo(() => {
    if (stats) return { svolte: stats.svolte, visite: stats.visite, contatti: stats.contatti, pianificate: stats.pianificate };
    const done = items.filter((i) => i.status === "svolto");
    return {
      svolte: done.length,
      visite: done.filter((i) => i.type === "visita_partner").length,
      contatti: done.filter((i) => CONTACT_TYPES.includes(i.type)).length,
      pianificate: items.filter((i) => i.status === "pianificato").length,
    };
  }, [items, stats]);

  const KPI_LABEL = { svolte: "Attività svolte", visite: "Visite", contatti: "Contatti", pianificate: "Pianificate" };
  const openKpi = async (key) => {
    setKpiSel({ key, label: KPI_LABEL[key], items: null, loading: true });
    const params = new URLSearchParams({ actor, limit: "3000" });
    if (key === "pianificate") params.set("status", "pianificato");
    else { params.set("status", "svolto"); if (key === "visite") params.set("kind", "visite"); if (key === "contatti") params.set("kind", "contatti"); }
    try {
      const r = await api.get(`/activities?${params.toString()}`);
      setKpiSel({ key, label: KPI_LABEL[key], items: r.data.items || [], loading: false });
    } catch {
      setKpiSel({ key, label: KPI_LABEL[key], items: [], loading: false });
    }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setNoteFeedback(null); setDialog(true); };
  const openEdit = (it) => {
    setEditing(it);
    setNoteFeedback(null);
    setForm({ ...EMPTY, ...it, agency_id: it.agency_id || "none", partner_id: it.partner_id || "none", prospect_id: it.prospect_id || "none", target_kind: it.prospect_id ? "prospect" : "partner", decision_maker: !!it.decision_maker, owner_id: it.owner_id || "none", data_prossima_azione: it.data_prossima_azione || "" });
    setDialog(true);
  };

  const save = async () => {
    setSaving(true);
    const isProspect = form.type !== "affiancamento_agenzia" && form.target_kind === "prospect";
    const payload = {
      type: form.type, date: form.date, time: form.time, status: form.status, outcome: form.outcome, notes: form.notes,
      actor, prossima_azione: form.prossima_azione, data_prossima_azione: form.data_prossima_azione || null,
      agency_id: form.agency_id === "none" ? null : form.agency_id,
      partner_id: isProspect || form.partner_id === "none" ? null : form.partner_id,
      prospect_id: isProspect && form.prospect_id !== "none" ? form.prospect_id : null,
      decision_maker: !!form.decision_maker,
      owner_id: form.owner_id === "none" ? null : form.owner_id,
    };
    try {
      if (editing) await api.put(`/activities/${editing.id}`, payload);
      else maybeFunnelPrompt(await api.post("/activities", payload));
      toast.success(editing ? "Attività aggiornata" : "Attività creata");
      setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [nsAct, setNsAct] = useState(null);
  const nonSvolta = (it) => setNsAct(it);
  const remove = async (it) => {
    if (!window.confirm("Eliminare questa attività?")) return;
    try { await api.delete(`/activities/${it.id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (key) => (v) => setForm((f) => ({ ...f, [key]: v?.target ? v.target.value : v }));
  const targetName = (it) => it.type === "affiancamento_agenzia" ? (it.agency_name || "—") : (it.partner_name || it.prospect_name || "—");
  const RefName = ({ it }) => it.partner_id
    ? <Link to={`/partners/${it.partner_id}`} className="font-semibold text-primary hover:underline" data-testid={`activity-ref-link-${it.id}`}>{targetName(it)}</Link>
    : <span className="font-semibold">{targetName(it)}</span>;

  if (loading)
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid={`activities-${actor}`}>
      <NonSvoltaDialog activity={nsAct} onClose={() => setNsAct(null)} onDone={load} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`h-3 w-3 rounded-full ${theme.dot}`} />
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">{title}</h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-lg border border-[#FDD003]/60 bg-[#FDD003]/[0.04] p-1.5" data-testid="agenda-group-crea">
              <QuickAdd onChanged={() => { load(); refreshProg(); }} show={["activity"]} />
              <Button size="sm" onClick={() => { setVisitaDefault({ partnerId: null, prospectId: null }); setVisitaOpen(true); }} data-testid="agenda-registra-visita-btn">
                <ClipboardCheck className="mr-2 h-4 w-4" /> Registra visita
              </Button>
            </div>
            <div data-testid="agenda-group-prospect">
              <QuickAdd onChanged={() => { load(); refreshProg(); }} show={["prospect"]} variant="default" />
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#FDD003]/60 bg-[#FDD003]/[0.04] p-1.5" data-testid="agenda-group-liste">
              <Button variant="outline" size="sm" onClick={() => setShowDaProg(true)} data-testid="agenda-da-programmare-btn"><CalendarClock className="mr-2 h-4 w-4" /> Visite da programmare{daProgrammare.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{daProgrammare.length}</Badge>}</Button>
              <Button variant="outline" size="sm" onClick={() => setShowProspect(true)} data-testid="agenda-prospect-recall-btn"><Clock className="mr-2 h-4 w-4" /> Prospect da ricontattare{prospectDue.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{prospectDue.length}</Badge>}</Button>
              <Button variant="outline" size="sm" onClick={() => setShowRecall(true)} data-testid="agenda-preventivi-recall-btn"><FileClock className="mr-2 h-4 w-4" /> Recall preventivi{recallItems.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{recallItems.length}</Badge>}</Button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="activities-scorecard">
        <ScoreKPI icon={ShieldCheck} label="Azioni su clienti"
          value={scorecard?.copertura_azioni != null ? `${scorecard.copertura_azioni}%` : "—"}
          hint={scorecard?.copertura_hint || "presidio clienti A/B"}
          pct={scorecard?.copertura_azioni} greenAt={scorecard?.copertura_target || 80} amberAt={(scorecard?.copertura_target || 80) * 0.6}
          testid="kpi-card-copertura" />
        <ScoreKPI icon={Radar} label="Prospect lavorati"
          value={scorecard?.prospecting_previste ? `${scorecard.prospecting_svolte}/${scorecard.prospecting_previste}` : (scorecard?.prospecting_svolte ?? "—")}
          hint={`prospect distinti lavorati${scorecard?.prospecting_pct != null ? ` · ${scorecard.prospecting_pct}%` : ""} · obiettivo ${scorecard?.prospecting_settimana || 0}/sett`}
          pct={scorecard?.prospecting_pct} greenAt={100} amberAt={70}
          testid="kpi-card-prospecting" />
        <ScoreKPI icon={CalendarClock} label="Pianificate prossime 2 sett."
          value={num(scorecard?.pianificate_2w ?? 0)}
          hint={`obiettivo ${scorecard?.pianificate_2w_target || 24} (visite/sett obiettivo agenzia × 2)${scorecard?.pianificate_2w_pct != null ? ` · ${scorecard.pianificate_2w_pct}%` : ""}`}
          pct={scorecard?.pianificate_2w_pct} greenAt={71} amberAt={40}
          testid="kpi-card-pianificate-2w" />
      </div>

      {showMonitor && (
        <Card className="p-6" data-testid="agencies-monitor-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Monitoraggio Agenzie</h2></div>
            <Select value={monitorPeriod} onValueChange={setMonitorPeriod}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="monitor-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mese">Ultimo mese</SelectItem>
                <SelectItem value="trimestre">Ultimo trimestre</SelectItem>
                <SelectItem value="anno">Ultimo anno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">Attività delle agenzie ordinate per numero di attività svolte nel periodo.</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead><TableHead>Agenzia</TableHead>
                  <TableHead className="text-center">Attività svolte</TableHead><TableHead className="text-center">Visite</TableHead>
                  <TableHead className="text-center">Contatti</TableHead><TableHead className="text-center">Pianificate</TableHead>
                  <TableHead className="text-center">Clienti</TableHead><TableHead className="text-center">Copertura presidio</TableHead>
                  <TableHead className="text-center">Conversioni mese</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(monitor?.agencies || []).map((a, i) => (
                  <TableRow key={a.agency_id} data-testid={`monitor-agency-${a.agency_id}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-semibold">{a.agency_name}</TableCell>
                    <TableCell className="text-center font-mono font-bold">{a.attivita_svolte}</TableCell>
                    <TableCell className="text-center font-mono">{a.visite}</TableCell>
                    <TableCell className="text-center font-mono">{a.contatti}</TableCell>
                    <TableCell className="text-center font-mono text-muted-foreground">{a.pianificate}</TableCell>
                    <TableCell className="text-center font-mono">{a.partner_totali}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={`text-[11px] ${a.copertura_presidio == null ? "" : a.copertura_presidio >= 80 ? "border-green-500/40 bg-green-500/15 text-green-400" : a.copertura_presidio >= 50 ? "border-amber-500/40 bg-amber-500/15 text-amber-400" : "border-red-500/40 bg-red-500/15 text-red-400"}`}>
                        {a.copertura_presidio != null ? `${a.copertura_presidio}%` : "N/D"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-mono">{a.conversioni_mese}</TableCell>
                  </TableRow>
                ))}
                {monitor && monitor.agencies.length === 0 && <TableRow><TableCell colSpan={9} className="py-10 text-center text-muted-foreground">Nessuna agenzia.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Card className="p-4">
        {totalCount > items.length && (
          <p className="mb-2 text-[11px] text-muted-foreground" data-testid="activities-list-note">Mostrando le ultime {num(items.length)} attività su {num(totalCount)} totali. I contatori qui sopra si riferiscono a tutte le attività; tocca una scheda per vedere il dettaglio.</p>
        )}
        <div className="mb-3 flex flex-wrap items-end gap-2" data-testid="activities-filters">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Agente</Label>
            <Select value={fltAgent} onValueChange={setFltAgent}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="flt-agent"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti gli agenti</SelectItem>{actAgents.map(([id, nm]) => <SelectItem key={id} value={id}>{nm}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Tipo attività</Label>
            <Select value={fltType} onValueChange={setFltType}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="flt-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti i tipi</SelectItem>{Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Da</Label>
            <Input type="date" value={fltFrom} onChange={(e) => setFltFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="flt-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">A</Label>
            <Input type="date" value={fltTo} onChange={(e) => setFltTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="flt-to" />
          </div>
          {(fltAgent !== "all" || fltType !== "all" || fltFrom || fltTo) && (
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setFltAgent("all"); setFltType("all"); setFltFrom(""); setFltTo(""); }} data-testid="flt-reset">Azzera filtri</Button>
          )}
          <span className="ml-auto text-[11px] text-muted-foreground" data-testid="flt-count">{filteredActs.length} risultati</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data / Ora</TableHead><TableHead>Tipo</TableHead><TableHead>Riferimento</TableHead>
                <TableHead>Stato</TableHead><TableHead>Esito</TableHead>
                {isAdmin && <TableHead>Responsabile</TableHead>}
                {!isAdmin && canAssign && <TableHead>Assegnatario</TableHead>}
                {!readOnly && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredActs.slice(0, 200).map((it) => (
                <TableRow key={it.id} data-testid={`activity-row-${it.id}`} className={`border-l-4 ${actor === "agenzia" && (it.agency_name || it.resolved_agency_name) ? "" : theme.border}`} style={actor === "agenzia" && (it.agency_name || it.resolved_agency_name) ? { borderLeftColor: agentHsl(it.agency_name || it.resolved_agency_name) } : undefined}>
                  <TableCell className="font-mono text-xs">{it.date}{it.time ? ` ${it.time}` : ""}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[11px]">{TYPE_LABEL[it.type]}</Badge></TableCell>
                  <TableCell className="max-w-[180px] truncate"><RefName it={it} /><div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">{actor === "agenzia" && (it.agency_name || it.resolved_agency_name) && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: agentHsl(it.agency_name || it.resolved_agency_name) }} />}{it.resolved_agency_name || "—"}</div><div className="mt-1"><AffiancamentoButton visit={it} onDone={load} /></div></TableCell>
                  <TableCell><Badge variant={it.status === "svolto" ? "default" : it.status === "non_svolta" ? "destructive" : "secondary"} className="text-[11px]" title={it.motivo_non_svolta || ""}>{it.status === "svolto" ? "Svolto" : it.status === "non_svolta" ? "Non svolta" : "Pianificato"}</Badge>{it.modified_by && <div className="mt-0.5 text-[10px] text-muted-foreground" data-testid={`activity-modified-${it.id}`}>mod. {it.modified_by}</div>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{it.outcome || "—"}</TableCell>
                  {isAdmin && <TableCell className="text-sm text-muted-foreground">{it.owner_name || "—"}</TableCell>}
                  {!isAdmin && canAssign && <TableCell className="text-sm text-muted-foreground">{it.owner_name || "—"}</TableCell>}
                  {!readOnly && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-activity-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                        {it.status === "pianificato" && <Button variant="ghost" size="icon" title="Segna come non svolta" onClick={() => nonSvolta(it)} data-testid={`nonsvolta-activity-${it.id}`}><Ban className="h-4 w-4 text-amber-500" /></Button>}
                        {canDelete && <Button variant="ghost" size="icon" onClick={() => remove(it)} data-testid={`delete-activity-${it.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filteredActs.length === 0 && <TableRow><TableCell colSpan={canAssign ? 7 : 6} className="py-10 text-center text-muted-foreground">Nessuna attività registrata.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      {!readOnly && (
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent data-testid="activity-dialog">
            <DialogHeader><DialogTitle>{editing ? "Modifica Attività" : "Nuova Attività"} · {theme.label}</DialogTitle><DialogDescription>Registra un'attività commerciale (contatti, affiancamenti, review).</DialogDescription></DialogHeader>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v, agency_id: "none", partner_id: "none" }))}>
                  <SelectTrigger data-testid="af-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Data</Label><DatePicker value={form.date} onChange={set("date")} data-testid="af-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Orario concordato</Label><Input type="time" value={form.time} onChange={set("time")} data-testid="af-time" /></div>
              {form.type === "affiancamento_agenzia" ? (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Agenzia</Label>
                  <Select value={form.agency_id} onValueChange={set("agency_id")}>
                    <SelectTrigger data-testid="af-agency"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">Tipo riferimento</Label>
                    <Select value={form.target_kind} onValueChange={(v) => setForm((f) => ({ ...f, target_kind: v, partner_id: "none", prospect_id: "none" }))}>
                      <SelectTrigger data-testid="af-target-kind"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="partner">Cliente / Partner</SelectItem><SelectItem value="prospect">Prospect</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {form.target_kind === "prospect" ? (
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs">Prospect</Label>
                      <Select value={form.prospect_id} onValueChange={set("prospect_id")}>
                        <SelectTrigger data-testid="af-prospect"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">—</SelectItem>{prospects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs">Partner</Label>
                      <Select value={form.partner_id} onValueChange={set("partner_id")}>
                        <SelectTrigger data-testid="af-partner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">—</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {form.target_kind === "prospect" && form.type !== "visita_partner" && (
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-black/20 p-2.5 sm:col-span-2" data-testid="af-dm-row">
                      <Checkbox checked={form.decision_maker} onCheckedChange={(v) => setForm((f) => ({ ...f, decision_maker: !!v }))} data-testid="af-dm-checkbox" />
                      <span className="text-xs">Parlato con il <strong>decision maker</strong> <span className="text-muted-foreground">(porta il prospect allo stadio “Decision maker”)</span></span>
                    </label>
                  )}
                </>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Stato</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger data-testid="af-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pianificato">Pianificato</SelectItem><SelectItem value="svolto">Svolto</SelectItem><SelectItem value="non_svolta">Non svolta</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Esito</Label><Input value={form.outcome} onChange={set("outcome")} placeholder="es. Positivo" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Prossima azione</Label><Input value={form.prossima_azione} onChange={set("prossima_azione")} data-testid="af-next" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Data prossima azione</Label><DatePicker value={form.data_prossima_azione} onChange={set("data_prossima_azione")} placeholder="Data prossima azione" /></div>
              {canAssign && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Assegna a (agente / responsabile)</Label>
                  <Select value={form.owner_id} onValueChange={set("owner_id")}>
                    <SelectTrigger data-testid="af-owner"><SelectValue placeholder="Seleziona assegnatario" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Non assegnato</SelectItem>
                      {assignees.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.role === "agency" ? `Agente: ${a.name}${a.agency_name ? ` · ${a.agency_name}` : ""}` : `Resp: ${a.name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Note</Label>
                  <span className="flex items-center text-[10px] text-muted-foreground" data-testid="note-ai-status">
                    {checkingNote ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Verifica AI…</> : <><Sparkles className="mr-1 h-3 w-3 text-primary" /> Verifica automatica AI</>}
                  </span>
                </div>
                <Textarea value={form.notes} onChange={set("notes")} data-testid="af-notes" placeholder="Descrivi cosa è stato fatto/emerso, l'esito e i prossimi passi." />
                {noteFeedback && (
                  <div className={`rounded-md border p-2 text-[11px] ${noteFeedback.sufficiente ? "border-green-500/40 bg-green-500/10 text-green-400" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`} data-testid="note-feedback">
                    {noteFeedback.sufficiente ? "✓ " : "✗ "}{noteFeedback.motivo}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog(false)}>Annulla</Button>
              <Button onClick={save} disabled={saving || !form.date} data-testid="save-activity-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {!readOnly && (
        <VisitaB2BDialog
          open={visitaOpen}
          onOpenChange={setVisitaOpen}
          partners={partners}
          prospects={prospects}
          managers={managers}
          actor={actor}
          defaultPartnerId={visitaDefault.partnerId}
          defaultProspectId={visitaDefault.prospectId}
          onSaved={load}
        />
      )}

      {!readOnly && (
        <ProspectRecontactDialog open={showProspect} onOpenChange={setShowProspect} items={prospectDue} onNuovaAttivita={openAttivitaFor} onRegistraVisita={openVisitaFor} />
      )}
      {!readOnly && (
        <RecallPreventiviDialog open={showRecall} onOpenChange={setShowRecall} items={recallItems} />
      )}
      {!readOnly && (
        <QuickAdd hideTriggers activityOpen={qaOpen} onActivityOpenChange={setQaOpen} partnerId={qaDefault.partnerId} partnerName={qaDefault.partnerName} prospectId={qaDefault.prospectId} prospectName={qaDefault.prospectName} onChanged={() => { load(); refreshProg(); }} show={["activity"]} />
      )}

      {!readOnly && (
        <VisiteDaProgrammareDialog open={showDaProg} onOpenChange={setShowDaProg} items={daProgrammare} onChanged={() => { load(); refreshProg(); }} actor={actor} ownerId={user?.role === "agency" ? user.id : null} onNuovaAttivita={openAttivitaFor} onRegistraVisita={openVisitaFor} />
      )}

      <Dialog open={!!kpiSel} onOpenChange={(o) => !o && setKpiSel(null)}>
        <DialogContent className="max-h-[80vh] overflow-hidden" data-testid="activities-kpi-dialog">
          <DialogHeader>
            <DialogTitle>{kpiSel?.label} <span className="text-sm font-normal text-muted-foreground">· {kpiSel?.loading ? "…" : (kpiSel?.items?.length || 0)}</span></DialogTitle>
            <DialogDescription>Dettaglio delle attività · {theme.label}.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {kpiSel?.loading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Data / Ora</TableHead><TableHead>Tipo</TableHead><TableHead>Riferimento</TableHead><TableHead>Stato</TableHead><TableHead>Esito</TableHead></TableRow></TableHeader>
              <TableBody>
                {(kpiSel?.items || []).map((it) => (
                  <TableRow key={it.id} data-testid={`kpi-activity-${it.id}`}>
                    <TableCell className="font-mono text-xs">{it.date}{it.time ? ` ${it.time}` : ""}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[11px]">{TYPE_LABEL[it.type]}</Badge></TableCell>
                    <TableCell className="max-w-[160px] truncate"><RefName it={it} /><div className="text-[10px] text-muted-foreground">{it.resolved_agency_name || "—"}</div></TableCell>
                    <TableCell><Badge variant={it.status === "svolto" ? "default" : it.status === "non_svolta" ? "destructive" : "secondary"} className="text-[11px]" title={it.motivo_non_svolta || ""}>{it.status === "svolto" ? "Svolto" : it.status === "non_svolta" ? "Non svolta" : "Pianificato"}</Badge>{it.modified_by && <div className="mt-0.5 text-[10px] text-muted-foreground" data-testid={`activity-modified-${it.id}`}>mod. {it.modified_by}</div>}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{it.outcome || "—"}</TableCell>
                  </TableRow>
                ))}
                {kpiSel && !kpiSel.loading && kpiSel.items.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Nessuna attività.</TableCell></TableRow>}
              </TableBody>
            </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}